import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ClockIcon, SearchIcon, UserIcon, UsersRoundIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChannelBase } from 'sdk';
import { appConfig } from 'shared';
import { useBreakpointBelow } from '~/hooks/use-breakpoints';
import { useDebounce } from '~/hooks/use-debounce';
import { ComboboxSearchInput } from '~/modules/common/combobox-search-input';
import { ContentPlaceholder } from '~/modules/common/content-placeholder';
import { EntityAvatar } from '~/modules/common/entity-avatar';
import { InputTriggerChevron } from '~/modules/common/form-fields/input-trigger-button';
import { inviteCheckQueryOptions } from '~/modules/memberships/query';
import { Badge } from '~/modules/ui/badge';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxPrimitive,
} from '~/modules/ui/combobox';
import { usersListQueryOptions } from '~/modules/user/query';
import { inviteNote } from '~/modules/user/use-invite-check';

const contentVariants = {
  hidden: { opacity: 0, y: -5, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, y: -5, scale: 0.98, transition: { duration: 0.1 } },
};

interface UserComboboxProps {
  value: string[];
  onChange: (emails: string[]) => void;
  channel: ChannelBase & { organizationId?: string };
}

export function UserCombobox({ value, onChange, channel }: UserComboboxProps) {
  const { t } = useTranslation();
  const isMobile = useBreakpointBelow('sm');
  const nameLabel = t('c:name').toLowerCase();

  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleUnselect = (email: string) => onChange(value.filter((selected) => selected !== email));

  const { data, isFetching } = useInfiniteQuery(usersListQueryOptions({ q: debouncedSearchQuery }));

  const items = data?.pages.flatMap((page) => page.items) ?? [];

  // The same check as typed addresses: a member is left out, one invited before shows when
  const resultEmails = items.map((user) => user.email.toLowerCase()).sort();
  const { data: outcomes } = useQuery({ ...inviteCheckQueryOptions(channel, resultEmails), placeholderData: keepPreviousData });
  const outcomeByEmail = new Map((outcomes ?? []).map((outcome) => [outcome.email, outcome]));

  return (
    <Combobox<string, true>
      multiple
      items={items.map((user) => user.email)}
      itemToStringLabel={(email) => email}
      itemToStringValue={(email) => email}
      value={value}
      onValueChange={onChange}
      inputValue={searchQuery}
      onInputValueChange={setSearchQuery}
      filter={() => true}
    >
      <ComboboxPrimitive.Trigger
        render={
          <button
            type="button"
            className="relative flex min-h-10 w-full cursor-pointer flex-wrap items-center gap-1 rounded-md border border-input bg-background p-1.5 pr-10 text-left"
          />
        }
      >
        {value.length ? (
          value.map((email) => (
            <Badge size="sm" variant="secondary" key={email} className="max-w-60">
              <span className="truncate">{email}</span>
              {/* biome-ignore lint/a11y/useSemanticElements: a native <button> cannot be nested inside the trigger <button>; this is an intentional removable-chip control. */}
              <span
                role="button"
                tabIndex={0}
                aria-label={t('c:remove')}
                className="focus-effect -m-1 ml-1 rounded-full py-1"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleUnselect(email);
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  event.stopPropagation();
                  handleUnselect(email);
                }}
              >
                <XIcon className="opacity-50 hover:opacity-100" />
              </span>
            </Badge>
          ))
        ) : (
          <span className="ml-1 text-sm">{t('c:search_users')}</span>
        )}
        <InputTriggerChevron className="absolute right-0 mx-2" />
      </ComboboxPrimitive.Trigger>

      <ComboboxContent>
        <ComboboxSearchInput
          value={searchQuery}
          onClear={() => setSearchQuery('')}
          isSearching={isFetching}
          placeholder={t('c:placeholder.type_input', { inputLabel: nameLabel })}
        />
        <ComboboxList>
          <AnimatePresence mode="wait">
            {!isFetching && !items.length ? (
              <motion.div key="empty-state" initial="hidden" animate="visible" exit="exit" variants={contentVariants}>
                <ComboboxEmpty>
                  {debouncedSearchQuery.length ? (
                    <ContentPlaceholder icon={SearchIcon} title="c:no_resource_found" titleOptions={{ resource: t('c:user_other').toLowerCase() }} />
                  ) : (
                    <ContentPlaceholder icon={UsersRoundIcon} title="c:invite_members_search.text" titleOptions={{ appName: appConfig.name }} />
                  )}
                </ComboboxEmpty>
              </motion.div>
            ) : (
              items.length > 0 && (
                <motion.div key="results" initial="hidden" animate="visible" exit="exit" variants={contentVariants}>
                  {items.map(({ id, name, email, entityType, thumbnailUrl }) => {
                    const outcome = outcomeByEmail.get(email.toLowerCase());
                    const alreadyMember = outcome?.outcome === 'member';
                    const invitedNote = outcome?.outcome === 'remind' ? inviteNote(outcome, t) : undefined;
                    return (
                      <ComboboxItem key={id} value={email} disabled={alreadyMember} className="group/user-item justify-between">
                        <div className="flex items-center gap-2">
                          <EntityAvatar type={entityType} className="size-8" id={id} name={name} url={thumbnailUrl} />
                          <span className="link-decoration group-active/user-item:link-decoration-strong truncate font-medium group-hover/user-item:underline">
                            {isMobile ? email : name}
                          </span>
                        </div>

                        <div className="flex min-w-0 items-center gap-2">
                          <Badge size="sm" variant="plain" className="hidden group-data-disabled/user-item:flex">
                            <UserIcon />
                            <span className="max-sm:hidden">{t('c:already_member')}</span>
                          </Badge>
                          {invitedNote && (
                            <Badge size="sm" variant="plain">
                              <ClockIcon />
                              <span className="max-sm:hidden">{invitedNote}</span>
                            </Badge>
                          )}
                          <span className="group-data-disabled/user-item:hidden group-data-selected/user-item:hidden max-sm:hidden">{email}</span>
                          <ComboboxItemIndicator />
                        </div>
                      </ComboboxItem>
                    );
                  })}
                </motion.div>
              )
            )}
          </AnimatePresence>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
